import { create } from 'zustand';
import { sortNotifications } from './adapters';
import type {
  Notification,
  NotificationScope,
} from './types';

type NotificationState = {
  items: Record<string, Notification>;
  buckets: Record<string, string[]>; // scopeKey -> notification ids
  activePlanTripId?: string;
  activePlanUnreadCount?: number;
};

type NotificationActions = {
  replaceScope: (scope: NotificationScope, scopeId: string | undefined, notifications: Notification[]) => void;
  upsertMany: (notifications: Notification[]) => void;
  markRead: (id: string) => void;
  markUnread: (id: string) => void;
  dismiss: (id: string) => void;
  markAllRead: (scope: NotificationScope, scopeId?: string) => void;
  clearScope: (scope: NotificationScope, scopeId?: string) => void;
  setActivePlanTripId: (tripId: string | undefined) => void;
  setActivePlanUnreadCount: (count: number | undefined) => void;
  resetStore: () => void;
};

const scopeKey = (scope: NotificationScope, scopeId?: string) => `${scope}:${scopeId ?? 'global'}`;

// React 18/19 + useSyncExternalStore requires selector results to be referentially stable
// for the same store snapshot. Some selectors build new arrays every time which can
// trigger "The result of getSnapshot should be cached" and update-depth loops.
// Cache computed notification lists per snapshot object identity.
const notificationsBySnapshotCache: WeakMap<object, Map<string, Notification[]>> = new WeakMap();

export const useNotificationStore = create<NotificationState & NotificationActions>((set, get) => ({
  items: {},
  buckets: {},
  activePlanTripId: undefined,
  activePlanUnreadCount: undefined,

  replaceScope: (scope, scopeId, notifications) => {
    const key = scopeKey(scope, scopeId);
    const nextIds = notifications.map((n) => n.id);

    set((state) => {
      const nextItems = { ...state.items };
      notifications.forEach((notification) => {
        nextItems[notification.id] = {
          ...nextItems[notification.id],
          ...notification,
        };
      });

      return {
        items: nextItems,
        buckets: {
          ...state.buckets,
          [key]: nextIds,
        },
      };
    });
  },

  upsertMany: (notifications) => {
    if (notifications.length === 0) return;
    set((state) => {
      const nextItems = { ...state.items };
      notifications.forEach((notification) => {
        nextItems[notification.id] = {
          ...nextItems[notification.id],
          ...notification,
        };
      });
      return { items: nextItems };
    });
  },

  markRead: (id) => {
    set((state) => {
      const existing = state.items[id];
      if (!existing) return state;
      return {
        ...state,
        items: {
          ...state.items,
          [id]: {
            ...existing,
            readAt: existing.readAt ?? Date.now(),
          },
        },
      };
    });
  },

  markUnread: (id) => {
    set((state) => {
      const existing = state.items[id];
      if (!existing) return state;
      if (!existing.readAt) return state;
      return {
        ...state,
        items: {
          ...state.items,
          [id]: {
            ...existing,
            readAt: null,
          },
        },
      };
    });
  },

  dismiss: (id) => {
    set((state) => {
      const existing = state.items[id];
      if (!existing) return state;
      return {
        ...state,
        items: {
          ...state.items,
          [id]: {
            ...existing,
            dismissedAt: Date.now(),
          },
        },
      };
    });
  },

  markAllRead: (scope, scopeId) => {
    const key = scopeKey(scope, scopeId);
    const ids = get().buckets[key] || [];
    set((state) => {
      const nextItems = { ...state.items };
      const now = Date.now();
      ids.forEach((id) => {
        const item = nextItems[id];
        if (item && !item.readAt) {
          nextItems[id] = { ...item, readAt: now };
        }
      });
      return { items: nextItems };
    });
  },

  clearScope: (scope, scopeId) => {
    const key = scopeKey(scope, scopeId);
    set((state) => {
      const nextItems = { ...state.items };
      const ids = state.buckets[key] || [];
      ids.forEach((id) => delete nextItems[id]);
      const nextBuckets = { ...state.buckets };
      delete nextBuckets[key];
      return { items: nextItems, buckets: nextBuckets };
    });
  },

  setActivePlanTripId: (tripId) => {
    set((state) => {
      if (state.activePlanTripId === tripId) {
        return state;
      }
      return { activePlanTripId: tripId };
    });
  },

  setActivePlanUnreadCount: (count) => {
    set((state) => {
      if (state.activePlanUnreadCount === count) {
        return state;
      }
      return { activePlanUnreadCount: count };
    });
  },

  resetStore: () =>
    set(() => ({
      items: {},
      buckets: {},
      activePlanTripId: undefined,
      activePlanUnreadCount: undefined,
    })),
}));

const isUnreadNotification = (notification: Notification): boolean =>
  notification.readAt == null || notification.readAt <= 0;

export const getNotificationsForScope = (
  state: NotificationState,
  scope: NotificationScope,
  scopeId?: string,
): Notification[] => {
  const key = scopeKey(scope, scopeId);

  const cachedForSnapshot = notificationsBySnapshotCache.get(state as unknown as object);
  const cached = cachedForSnapshot?.get(key);
  if (cached) {
    return cached;
  }

  const ids = state.buckets[key] || [];
  const notifications = ids
    .map((id) => state.items[id])
    .filter((n): n is Notification => Boolean(n) && !n.dismissedAt);

  const sorted = sortNotifications(notifications);

  const nextMap = cachedForSnapshot ?? new Map<string, Notification[]>();
  nextMap.set(key, sorted);
  if (!cachedForSnapshot) {
    notificationsBySnapshotCache.set(state as unknown as object, nextMap);
  }

  return sorted;
};

export const selectNotifications =
  (scope: NotificationScope, scopeId?: string) =>
    (state: NotificationState & NotificationActions) =>
      getNotificationsForScope(state, scope, scopeId);

export const selectNotificationCount =
  (scope: NotificationScope, scopeId?: string) =>
    (state: NotificationState & NotificationActions) =>
      getNotificationsForScope(state, scope, scopeId).filter(isUnreadNotification).length;

/**
 * Cache for trip-filtered notifications to maintain referential stability.
 * Uses a WeakMap keyed by state snapshot to avoid memory leaks.
 */
const notificationsForTripCache: WeakMap<object, Map<string, Notification[]>> = new WeakMap();

/** Referentially stable empty list for `useSyncExternalStore` / Zustand selectors (never `return []` inline). */
const EMPTY_TRIP_NOTIFICATIONS: Notification[] = [];

/**
 * Get notifications from the global 'trip' bucket filtered by a specific tripId.
 * This allows the Plan screen to read from the same source as Trips screen
 * without needing a separate sync call.
 */
export const getNotificationsForTrip = (
  state: NotificationState,
  tripId: string | undefined,
): Notification[] => {
  if (!tripId) return EMPTY_TRIP_NOTIFICATIONS;
  
  const cacheKey = `forTrip:${tripId}`;
  const cachedForSnapshot = notificationsForTripCache.get(state as unknown as object);
  const cached = cachedForSnapshot?.get(cacheKey);
  if (cached) {
    return cached;
  }

  // Get all notifications from the global 'trip:global' bucket
  const globalNotifications = getNotificationsForScope(state, 'trip', undefined);
  
  // Filter by tripId - notifications store tripId in scopeId or meta.tripId
  const filtered = globalNotifications.filter((n) => {
    return n.scopeId === tripId || (n.meta?.tripId as string) === tripId;
  });

  const nextMap = cachedForSnapshot ?? new Map<string, Notification[]>();
  nextMap.set(cacheKey, filtered);
  if (!cachedForSnapshot) {
    notificationsForTripCache.set(state as unknown as object, nextMap);
  }

  return filtered;
};

export const selectNotificationsForTrip =
  (tripId: string | undefined) =>
    (state: NotificationState & NotificationActions) =>
      getNotificationsForTrip(state, tripId);

export const selectNotificationCountForTrip =
  (tripId: string | undefined) =>
    (state: NotificationState & NotificationActions) =>
      getNotificationsForTrip(state, tripId).filter(isUnreadNotification).length;

const belongsToTrip = (notification: Notification, tripId: string) =>
  notification.scopeId === tripId || (notification.meta?.tripId as string | undefined) === tripId;

const countUnreadTripNotificationsInItems = (
  state: NotificationState,
  tripId?: string,
): number =>
  Object.values(state.items).filter((notification) => {
    if (notification.scope !== 'trip') return false;
    if (notification.dismissedAt) return false;
    if (!isUnreadNotification(notification)) return false;
    return tripId ? belongsToTrip(notification, tripId) : true;
  }).length;

export const selectPlanTabBadgeCount = (state: NotificationState & NotificationActions): number =>
  typeof state.activePlanUnreadCount === 'number'
    ? state.activePlanUnreadCount
    : state.activePlanTripId
      ? countUnreadTripNotificationsInItems(state, state.activePlanTripId)
      : countUnreadTripNotificationsInItems(state);
